import Button from 'antd/lib/button';
import Space from 'antd/lib/space';
import React from 'react';
import { AppComponentProps, QuickStartCISetupStep, QuickStartCISetupViewProps } from '../../../typescriptHelpers';
import { InfoTip, setupModuleStyle } from '../../setup/shared';
import { ApiOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { FormInstance, useForm } from 'antd/lib/form/Form';
import { selectAppStoreIntegration, selectAppStoreType } from '../../../selectors/selectors';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../reducers/reducers';
import { authTypeForIntegrationType, displayNameForIntegrationType } from '../../../helpers/integrationHelpers';
import { globalMessage } from '../../../utils/globalMessage';
import {
    connectDataForIntegrationType,
    requiresFileUpload,
    s3KeyFromFileUpload,
    validateFormValuesForIntegrationType,
} from '../../ConnectIntegrationModal';
import { IntegrationCapability, IntegrationType } from '../../../types';
import {
    FieldType,
    formValidAndRequiredFieldsNotEmpty,
    integrationModalContentUsingFormInstance,
} from '../../../helpers/formHelpers';
import { UploadFile } from 'antd/lib/upload';
import UploadFileComponent from '../../UploadFileComponent';
import { isRequiredRule } from '../../../helpers/integrationFormHelpers';
import {
    CreateIntegrationRequestDataType,
    CreateIntegrationRequest,
    tryCreateIntegrationRequest,
} from '../../../actions/integrations';
import { getIntegrationTypeForAppStoreType } from '../../../helpers';
import { QuestionsInfoSection } from '../QuestionsInfoSection';
import { IntegrationsSetupList } from '../../sharedComponents/IntegrationsSetupList';
import { displayConsoleNameForAppStoreType } from '../../../helpers/displayHelpers';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';

const SetupViewComponent: React.FC<QuickStartCISetupViewProps> = ({ appId, orgId }) => {
    const [form] = useForm();
    const dispatch = useDispatch();
    const [fileList, setFileList] = React.useState<UploadFile<unknown>[]>([]);
    const [formIsValid, setFormIsValid] = React.useState(false);

    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, appId));

    const appStoreIntegration = useSelector((state: RootState) => selectAppStoreIntegration(state, appId));

    const integrationType = getIntegrationTypeForAppStoreType(appStoreType);
    const fields =
        integrationType ?
            [
                ...connectDataForIntegrationType(integrationType),
                {
                    initialValue: false,
                    name: 'shouldAddKeyToBuildSecrets',
                    fieldType: FieldType.Checkbox,
                    data: `Upload credentials to GitHub repository secrets`,
                },
            ]
        :   [];

    const authType = authTypeForIntegrationType(integrationType);

    const addIntegration = (integrationType: IntegrationType, data: CreateIntegrationRequestDataType) => {
        const request: CreateIntegrationRequest = {
            orgId,
            appId,
            integrationId: integrationType,
            data,
        };

        const authType = authTypeForIntegrationType(integrationType);

        if (authType) {
            dispatch(
                tryCreateIntegrationRequest(authType, {
                    ...request,
                    isQuickStartCI: false,
                }),
            );
        } else {
            globalMessage.error('Unrecognized auth type for integration type');
        }
    };

    const onUpdateFileList = (integrationId: IntegrationType, fileList: UploadFile<unknown>[]) => {
        setFileList(fileList);

        const requiredFieldsForIntegrationId = connectDataForIntegrationType(integrationId)
            .filter((field) => field.rules.find(isRequiredRule))
            .map((field) => field.name);

        const formValid = formValidAndRequiredFieldsNotEmpty(form, requiredFieldsForIntegrationId);

        setFormIsValid(formValid);
    };

    const onFieldsChange = (integrationId: IntegrationType, form: FormInstance) => {
        const requiredFieldsForIntegrationId = connectDataForIntegrationType(integrationId)
            .filter((field) => field.rules.find(isRequiredRule))
            .map((field) => field.name);
        const formValid = formValidAndRequiredFieldsNotEmpty(form, requiredFieldsForIntegrationId);
        setFormIsValid(formValid);
    };

    const onFinish = (_, integrationType: IntegrationType, form: FormInstance) => {
        form.validateFields().then((values) => {
            const initialData = validateFormValuesForIntegrationType(integrationType, values);
            let data = initialData;
            if (needsFileUpload) {
                data = {
                    ...initialData,
                    s3Key: s3KeyFromFileUpload(fileList),
                };
            }

            if (values.shouldAddKeyToBuildSecrets === true) {
                data = {
                    ...data,
                    shouldAddKeyToBuildSecrets: true,
                };
            }

            addIntegration(integrationType, data);
        });
    };

    const needsFileUpload = requiresFileUpload(integrationType);

    const isValid = () => {
        if (!needsFileUpload) {
            return formIsValid;
        } else {
            const s3Key = s3KeyFromFileUpload(fileList);

            return s3Key && formIsValid;
        }
    };

    const updateIntegrationIsLoading = useIsLoading(authType);
    const updateIntegrationSuccess = useUpdated(authType);
    const updateIntegrationError = useError(authType);

    React.useEffect(() => {
        if (updateIntegrationSuccess) {
            globalMessage.success(`Successfully updated ${displayNameForIntegrationType(integrationType)}!`);
        }
    }, [updateIntegrationSuccess]);

    React.useEffect(() => {
        if (updateIntegrationError) {
            globalMessage.error(updateIntegrationError);
        }
    }, [updateIntegrationError]);

    if (appStoreIntegration) {
        return (
            <IntegrationsSetupList
                appId={appId}
                orgId={orgId}
                relatedCapabilities={[IntegrationCapability.AppStore]}
                filterIntegrations={(integrationType) => {
                    return integrationType === IntegrationType.Apple || integrationType === IntegrationType.Google;
                }}
            />
        );
    }

    return (
        <div
            style={{
                ...setupModuleStyle,
            }}
        >
            <Space direction="vertical" style={{ width: '100%' }} size={20}>
                {fields &&
                    integrationModalContentUsingFormInstance(
                        form,
                        'vertical',
                        integrationType,
                        null,
                        fields,
                        onFieldsChange,
                        onFinish,
                    )}

                {needsFileUpload && (
                    <UploadFileComponent
                        orgId={orgId}
                        appId={appId}
                        integrationId={integrationType}
                        fileList={fileList}
                        onUpdateFileList={onUpdateFileList}
                    />
                )}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                    }}
                >
                    <Button
                        loading={updateIntegrationIsLoading}
                        type="primary"
                        disabled={!isValid()}
                        icon={<ArrowRightOutlined />}
                        onClick={() => form.submit()}
                    >
                        Save
                    </Button>
                </div>
            </Space>
        </div>
    );
};

const InfoViewComponent: React.FC<AppComponentProps> = ({ appId }) => {
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, appId));

    return (
        <Space direction="vertical">
            <InfoTip>
                The Quickstart wizard will pull in details about your app from{' '}
                {displayConsoleNameForAppStoreType(appStoreType)} as needed to automatically configure your workflow.
            </InfoTip>
            <InfoTip>
                The wizard can also automatically upload certain required fields to GitHub’s encrypted repo secrets for
                an even more streamlined setup.
            </InfoTip>
            <QuestionsInfoSection />
        </Space>
    );
};

export const AppStoreIntegration: QuickStartCISetupStep = {
    icon: () => ApiOutlined,
    headerTitle: (_platform, appStoreType) => {
        return `Connect to ${displayConsoleNameForAppStoreType(appStoreType)}.`;
    },
    SetupViewComponent,
    InfoViewComponent,
    isCompleteSelector: () => {
        return true;
    },
    isSkippable: true,
};
